import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ProcessStage {
  step: string;
  title: string;
  description: string;
}
const stages: ProcessStage[] = [{
  step: "01",
  title: "Discovery and goals",
  description: "I establish what success means for your business—whether enquiries, sales or target market expansion—and record a starting baseline from your current rankings, traffic and conversion data."
}, {
  step: "02",
  title: "SEO audit and opportunity assessment",
  description: "I conduct a detailed technical, content, keyword and competitor review tailored to your site size, interpreted commercially to find genuine growth areas rather than producing generic automated exports."
}, {
  step: "03",
  title: "Prioritised roadmap",
  description: "Findings are turned into a phased action plan ranked by impact versus effort, defining clear responsibilities across technical fixes, content and authority work, alongside how progress will be measured."
}, {
  step: "04",
  title: "Implementation and collaboration",
  description: "I collaborate directly with your developers and content writers, implement agreed changes myself where CMS access and scope allow, and review updates carefully as they go live."
}, {
  step: "05",
  title: "Measurement and reporting",
  description: "I deliver clear written reports on organic visibility, qualified traffic and agreed business KPIs using Google Search Console and analytics data, each paired with concrete next steps."
}];
export default function Process() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="process" aria-labelledby="process-heading" ref={ref} className="bg-muted/30 py-20 lg:py-28 border-b border-border transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
            Structured Execution
          </p>
          <h2 id="process-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            My Five-Stage SEO Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A transparent, methodical consulting framework designed to eliminate guesswork,
            focus resources on high-impact priorities, and maintain accountability at every stage.
          </p>
        </div>

        {/* Numbered Stages in a Connected Layout */}
        <ol className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4 list-none p-0 m-0">
          {stages.map((stage, index) => <li key={stage.step} className={`relative bg-card text-card-foreground border border-border rounded-xl p-6 flex flex-col justify-between transition-all duration-500 hover:border-primary/40 hover:shadow-sm ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${index * 100}ms`
        }}>
              <div>
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-xs font-bold text-primary px-2.5 py-1 rounded bg-secondary/60">
                    Stage {stage.step}
                  </span>
                  <span className="font-mono text-2xl font-bold text-muted-foreground/30">
                    {stage.step}
                  </span>
                </div>

                <h3 className="font-serif text-lg font-semibold text-foreground mb-3 leading-snug">
                  {stage.title}
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {stage.description}
                </p>
              </div>

              {/* Connecting indicator bar on desktop */}
              <div className="hidden lg:block mt-6 pt-4 border-t border-border/60 text-xs font-medium text-muted-foreground" aria-hidden="true">
                {index < stages.length - 1 ? "Next: Step 0" + (index + 2) : "Continuous Review"}
              </div>
            </li>)}
        </ol>
      </div>
    </section>;
}